import { byId as departmentsById } from '../data/departments.js';
import { OBJECTS } from '../data/objects.js';
import { STAFF_BY_ID } from '../data/staff.js';
import { describe as describeWriteup } from '../sim/writeups.js';
import { CONSEQUENCES, OUTCOMES } from '../sim/noticed.js';
import { formatTime } from '../sim/clock.js';

/**
 * One screen at the end of a shift, readable in five seconds.
 *
 * Six blocks, in the order a person actually cares about them: what you set,
 * what went ahead without you, where that left you with all seven departments,
 * what you now owe and to whom, what is on your file, and what the building
 * noticed.
 *
 * The ghost column is the reason this screen exists. Every other number here
 * the player watched happen. The events that resolved while they were in the
 * ballroom are invisible from inside the game, and a cost you never see is a
 * cost that teaches nothing — so the board you walked away from gets the same
 * space as the room you set.
 *
 * The last block is that argument carried through to the other invisible half.
 * The file says what you were caught for. It cannot say what you got away with,
 * and getting away with it is most of what happens in a permissive world: the
 * one who was facing your way and shrugged, the one who watched you for eight
 * seconds and went back to work. A player who only ever learns the rule by
 * being written up for it has learned the rule far too late to use it.
 */

const COSTLY = new Set(CONSEQUENCES);

/**
 * Near misses shown before the rest are counted rather than listed.
 *
 * Five seconds is the budget for the whole screen, and a busy shift can leave
 * a dozen people holding something they never acted on. The closest calls are
 * the ones worth reading; the tail is worth knowing the size of.
 */
const NEAR_MISSES = 5;

/**
 * How somebody came by what they believe.
 *
 * Three ways, because perception has three, and the words are the words a
 * person would use rather than the strings the simulation passes around.
 */
const HOW = {
  saw: 'watched',
  heard: 'heard',
  after: 'walked in on',
};

/**
 * What they think they saw, as something that can follow any of the three.
 *
 * Every one of these is a gerund, which is the only form that reads after
 * "watched", "heard" and "walked in on" alike. The table is vocabulary, not
 * script: what appears on screen is assembled from the claim, and a kind
 * nobody has written a phrase for falls through to its own name rather than to
 * a guess about what it meant.
 */
const CLAIM = {
  take: 'you helping yourself',
  break: 'something breaking',
  throw: 'you throwing something',
  tip: 'a cart going over',
  ride: 'you riding a cart',
  climb: 'you climbing something',
  trespass: 'you where you have no business being',
  uniform: 'you on the marble in blacks',
  // The quietest line on the block, and it is here because a drop is a thing
  // people hear rather than a thing nothing records. It will nearly always
  // read as "never thought twice", which is the point of it.
  drop: 'you putting something down',
  civilian: 'you back here in your own clothes',
  impaired: 'you not quite with it',
  scent: 'what you had been at',
};

/** The name of a thing, when the claim names one. */
const thing = (kind) =>
  kind ? (OBJECTS[kind]?.name ?? String(kind).replace(/-/g, ' ')).toLowerCase() : null;

/** What one witness would say they noticed. */
/** What somebody else was seen doing, in the same gerund-free form a sentence needs. */
const THEIRS = { take: 'take', break: 'break', throw: 'throw' };

function describeClaim(entry) {
  const named = thing(entry.detail);
  // Somebody else, by name: "watched Ryan "Rye Guy" take a pastry".
  if (entry.about) {
    const act = THEIRS[entry.what] ?? String(entry.what).replace(/-/g, ' ');
    return `${HOW[entry.mode] ?? 'noticed'} ${entry.about} ${act}${named ? ` a ${named}` : ''}`;
  }
  const what =
    entry.what === 'break' && named
      ? `a ${named} breaking`
      : entry.what === 'throw' && named
        ? `you throwing a ${named}`
        : entry.what === 'take' && named
          ? `you helping yourself to the ${named}`
          : entry.what === 'wildlife' && named
            ? `a ${named} where it should not be`
            : CLAIM[entry.what] ?? String(entry.what).replace(/-/g, ' ');
  return `${HOW[entry.mode] ?? 'noticed'} ${what}`;
}

/**
 * How sure they got, in words.
 *
 * A player cannot do anything with 0.62. What they can do something with is
 * knowing the difference between somebody who glanced and somebody who is
 * certain, because those are the two ends of whether it was worth the risk.
 */
function sureness(peak) {
  if (peak < 0.25) return { label: 'a glimpse', band: 'faint' };
  if (peak < 0.5) return { label: 'half sure', band: 'low' };
  if (peak < 0.75) return { label: 'fairly sure', band: 'high' };
  return { label: 'certain', band: 'certain' };
}

/**
 * The kinds of not saying anything, which is most of what a shift is.
 *
 * A let-go entry carries the reaction that produced it, so the record can tell
 * the difference between a colleague who covered for you, one who is keeping
 * count, and one who has gone off to tell the whole section. All three cost you
 * nothing on the card and they are three completely different facts about your
 * week — which is the argument this whole screen was built on.
 */
const QUIETLY = {
  covers: 'saw it, said nothing, never will',
  'squares-it': 'did not see a thing, and you owe them one',
  remembers: 'said nothing, and is keeping count',
  'tells-crew': 'told the section, and nobody above it',
  'tells-everyone': 'mentioned it to every department but their own',
  // Not about you at all, which is why it is the only line here without you in it.
  'deals-with-it': 'went out to deal with it',
  // A complaint that went to a manager and stayed with the hotel, because
  // nobody saw you with it.
  absorbed: 'complained about it, and not about you',
  // Somebody who is not staff, and made a note of it rather than doing anything.
  collects: 'made a note of it on a tablet',
  // The one you covered for at the end, and what that bought you.
  owes: 'let it go, and you both know why',
};

/**
 * Everything noticed off the clock, as a line per occasion: coming in, and any
 * other time you were about without punching in. People, not entries — one
 * valet who noticed both your clothes and where you were standing is one
 * person who saw you come in.
 */
export function offClockLines(entries, clockedInAt = null) {
  const cameIn = new Set();
  const otherwise = new Set();
  for (const entry of entries) {
    const before = clockedInAt === null || clockedInAt === undefined || entry.opened <= clockedInAt;
    (before ? cameIn : otherwise).add(entry.by);
  }
  const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
  const lines = [];
  if (cameIn.size) lines.push(`${people(cameIn.size)} saw you come in.`);
  if (otherwise.size) lines.push(`${people(otherwise.size)} saw you about off the clock.`);
  return lines;
}

/** And what came of it. */
function outcomeOf(entry) {
  switch (entry.outcome) {
    case OUTCOMES.FILED:
      return `written up: ${describeWriteup(entry.reason)}`;
    case OUTCOMES.COMPLAINT:
      return 'went and told a manager';
    case OUTCOMES.NAMED:
      return 'spoke up, nothing stuck';
    default:
      // A deliberate let-go says which kind it was. Everything else is the two
      // shapes of getting away with it, and the difference between them is the
      // difference between a close call and a shrug.
      if (QUIETLY[entry.reason]) return QUIETLY[entry.reason];
      if (entry.subject) return entry.stirred ? 'kept an eye on it' : 'never thought twice';
      return entry.stirred ? 'watched you a while, then let it go' : 'never thought twice';
  }
}

export function createShiftSummary({ parent, onClose }) {
  const root = document.createElement('div');
  root.className = 'summary';
  root.hidden = true;
  parent.append(root);

  function close() {
    root.hidden = true;
    onClose?.();
  }

  return {
    root,
    get open() {
      return !root.hidden;
    },
    close,

    show({
      day,
      dayName,
      shift,
      who,
      act = null,
      set,
      ghosts,
      standing,
      favors,
      writeups,
      ghostBaseline,
      noticed = [],
      clockedInAt = null,
      sightings = [],
    }) {
      const setRows = set.length
        ? set
            .map(
              (record) => `<div class="sum-row">
                <span class="sum-grade" data-grade="${record.grade}">${record.grade}</span>
                <span class="sum-name">${record.code} · ${record.title}</span>
                <span class="sum-note">${record.onTime ? 'on time' : 'late'}</span>
              </div>`
            )
            .join('')
        : '<p class="sum-none">Nothing set.</p>';

      const ghostRows = ghosts.length
        ? ghosts
            .map(
              (record) => `<div class="sum-row">
                <span class="sum-grade" data-grade="${record.letter}" data-ghost="yes">${record.letter}</span>
                <span class="sum-name">${record.code} · ${record.title}</span>
                <span class="sum-note">${departmentsById[record.owner].name}</span>
              </div>`
            )
            .join('')
        : '<p class="sum-none">Everything posted was covered.</p>';

      const standingRows = standing.length
        ? standing
            .map(
              (row) => `<div class="sum-move" data-tone="${row.delta > 0 ? 'good' : 'bad'}">
                <span class="sum-dept">${departmentsById[row.department].name}</span>
                <span class="sum-delta">${row.delta > 0 ? '+' : ''}${row.delta.toFixed(1)}</span>
                <span class="sum-now">now ${Math.round(row.to)}</span>
                <span class="sum-causes">${row.causes
                  .slice(0, 3)
                  .map((cause) => cause.reason)
                  .join(' · ')}</span>
              </div>`
            )
            .join('')
        : '<p class="sum-none">Nothing moved.</p>';

      const favorRows = favors.length
        ? favors
            .map(
              (favor) => `<div class="sum-row">
                <span class="sum-name">${STAFF_BY_ID[favor.staffId].name}</span>
                <span class="sum-note">${departmentsById[favor.department].name} · ${favor.note}</span>
              </div>`
            )
            .join('')
        : '<p class="sum-none">You owe nobody.</p>';

      const writeupRows = writeups.length
        ? writeups
            .map(
              (entry) => `<div class="sum-row">
                <span class="sum-name">${describeWriteup(entry.reason)}</span>
                <span class="sum-note">${entry.at !== null ? formatTime(entry.at) : ''} ${entry.detail ?? ''}</span>
              </div>`
            )
            .join('')
        : '<p class="sum-none">Clean.</p>';

      /*
       * What the building noticed, in two groups and in that order.
       *
       * The sim hands these over already sorted — what it cost you, oldest
       * first, then what it did not, closest call first — so the split here is
       * a cut rather than a second opinion about which is which.
       */
      const costly = noticed.filter((entry) => COSTLY.has(entry.outcome));
      /*
       * Near misses on the clock are listed one by one: each is somebody who
       * could have done something and did not, and which is the lesson.
       * Near misses off the clock are one line — they could never have cost
       * anything, and seven rows of people watching you walk in from the car
       * park is a wall where the idea is one sentence: they were watching.
       */
      const missed = noticed.filter((entry) => !COSTLY.has(entry.outcome) && entry.onDuty !== false);
      const offClock = offClockLines(
        noticed.filter((entry) => !COSTLY.has(entry.outcome) && entry.onDuty === false),
        clockedInAt
      );
      const shown = missed.slice(0, NEAR_MISSES);
      const rest = missed.length - shown.length;

      const noticedRow = (entry) => {
        const sure = sureness(entry.peak);
        return `<div class="sum-seen" data-outcome="${entry.outcome}">
            <span class="sum-name">${entry.who ?? 'Somebody'}</span>
            <span class="sum-saw">${describeClaim(entry)}</span>
            <span class="sum-sure" data-band="${sure.band}">${sure.label}</span>
            <span class="sum-outcome">${outcomeOf(entry)}</span>
          </div>`;
      };

      const costlyRows = costly.length
        ? costly.map(noticedRow).join('')
        : '<p class="sum-none">Nobody said anything to anybody.</p>';

      const offClockRows = offClock.map((text) => `<p class="sum-offclock">${text}</p>`).join('');
      const missedRows = shown.length
        ? offClockRows +
          shown.map(noticedRow).join('') +
          (rest > 0
            ? `<p class="sum-none">And ${rest} more nobody did anything about.</p>`
            : '')
        : offClockRows || '<p class="sum-none">Nobody noticed a thing all shift.</p>';

      // What you noticed of the desert, which is the only line on this screen
      // that is not about what the hotel thinks of you.
      const wildlifeLine = sightings.length
        ? `<p class="sum-foot">Out there today: ${sightings
            .map((entry) => (entry.firstTime ? `${entry.label} (a first)` : entry.label))
            .join(', ')}.</p>`
        : '';

      root.innerHTML = `
        <div class="summary-card">
          <header>
            <span class="sum-day">DAY ${day} · ${dayName.toUpperCase()}${
              who ? ` · ${who.toUpperCase()}` : ''
            }</span>
            ${act ? `<span class="sum-act">ACT ${act.n} · ${act.name.toUpperCase()}</span>` : ''}
            <h2>${shift.name} · ${shift.start}–${shift.end}</h2>
          </header>
          <div class="sum-grid">
            <section>
              <h3>Set</h3>${setRows}
              <h3>Went ahead without you</h3>${ghostRows}
              <p class="sum-foot">Your rooms are running at ${Math.round(
                ghostBaseline * 100
              )}%. The crew mirrors it.</p>
            </section>
            <section>
              <h3>Where that leaves you</h3>${standingRows}
              <h3>Owed</h3>${favorRows}
              <h3>On your file</h3>${writeupRows}
            </section>
          </div>
          <section class="sum-wide">
            <h3>What the building noticed</h3>${costlyRows}
            <h3>What you got away with</h3>${missedRows}
            ${wildlifeLine}
          </section>
          <button class="board-close" type="button">Close  [Esc]</button>
        </div>`;
      root.querySelector('.board-close').addEventListener('click', close);
      root.hidden = false;
    },
  };
}
